import {from, lastValueFrom, mergeMap} from 'rxjs';

import {addQueryToPath} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {CancelToken} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {serializeAttachment} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {
  deleteAsync,
  fetchApiAsync,
  postApiAsync,
  putApiAsync,
} from '../../../../libs/shared/core/src/services/http/http';
import {
  ApiVacationResponder,
  ApiVacationResponders,
  VacationResponderModel,
} from '../../../../src/models/api/vacationResponderModel';

/*
 * Helpers.
 */

export async function fetchVacationResponderAsync(
  companyUrl: string,
  vacationResponderId: number,
  cancelToken: CancelToken,
) {
  return fetchApiAsync(
    ApiVacationResponder,
    `${companyUrl}/vacation_responders/${encodeURIComponent(vacationResponderId)}`,
    {
      cancelToken,
    },
  );
}

export async function fetchVacationRespondersAsync(
  companyUrl: string,
  namespace: string,
  cancelToken: CancelToken,
  {limit}: {limit?: number} = {},
) {
  return fetchApiAsync(
    ApiVacationResponders,
    addQueryToPath(`${companyUrl}/namespaces/${encodeURIComponent(namespace)}/vacation_responders`, {limit}),
    {cancelToken},
  );
}

export async function createVacationResponderAsync(
  companyUrl: string,
  vacationResponder: VacationResponderModel,
): Promise<VacationResponderModel> {
  const url = `${companyUrl}/vacation_responders`;
  const body = {
    namespace: vacationResponder.namespace,
    name: vacationResponder.name,
    body: vacationResponder.body,
    enabled: vacationResponder.isAutoResponderEnabled,
    only_contacts: vacationResponder.onlyContacts,
    start_at: vacationResponder.startAt,
    end_at: vacationResponder.endAt,
    attachments: vacationResponder.attachments.map((a) => serializeAttachment(a)),
    channel_ids: vacationResponder.channelIds,
  };
  return postApiAsync(ApiVacationResponder, url, body);
}

export async function updateVacationResponderAsync(
  companyUrl: string,
  vacationResponderId: number,
  vacationResponder: VacationResponderModel,
): Promise<VacationResponderModel> {
  const url = `${companyUrl}/vacation_responders/${encodeURIComponent(vacationResponderId)}`;
  const body = {
    name: vacationResponder.name,
    body: vacationResponder.body,
    enabled: vacationResponder.isAutoResponderEnabled,
    only_contacts: vacationResponder.onlyContacts,
    start_at: vacationResponder.startAt,
    end_at: vacationResponder.endAt,
    attachments: vacationResponder.attachments.map((a) => serializeAttachment(a)),
    channel_ids: vacationResponder.channelIds,
  };

  return putApiAsync(ApiVacationResponder, url, body);
}

export async function deleteVacationResponderAsync(
  companyUrl: string,
  vacationResponderId: number,
): Promise<void> {
  const url = `${companyUrl}/vacation_responders/${encodeURIComponent(vacationResponderId)}`;
  await deleteAsync(url);
}

const bulkDeleteConcurrency = 3;

export async function bulkDeleteVacationRespondersAsync(
  companyUrl: string,
  vacationResponderIds: ReadonlyArray<number>,
) {
  const delete$ = from(vacationResponderIds).pipe(
    mergeMap(
      (vacationResponderId) => deleteVacationResponderAsync(companyUrl, vacationResponderId),
      bulkDeleteConcurrency,
    ),
  );
  await lastValueFrom(delete$);
}
